import request, { GET } from '../../../store/request'
import { get } from '../../../store/localstorage'
import { localDayBoundsUTC } from '../../../store'

const authConfig = () => ({ headers: { Authorization: `Token ${get('auth_token')}` }, withCredentials: true })

export const exportCsv = async (filter) => {
  const b = localDayBoundsUTC(filter.start, filter.end)   // full LOCAL calendar day in UTC (issue #2)
  const config = {
    ...authConfig(), responseType: 'blob',
    params: { units: (filter.units || []).join(','), start: b.start, end: b.end },
  }
  const resp = await request(GET, '/apiv2/traffic_metrics/export.csv', {}, config)
  const url = URL.createObjectURL(resp.data)
  const a = document.createElement('a'); a.href = url
  a.download = `traffic_${filter.start}_${filter.end}.csv`; a.click()
  URL.revokeObjectURL(url)
}

// Capture each chart (title + recharts SVG) from the off-screen export container,
// so every export includes all four plots regardless of the active tab. SVG is
// serialized to PNG (reliable for recharts' plain SVG); the title comes from the DOM.
const captureCharts = async () => {
  const cards = Array.from(document.querySelectorAll('#tm-export-charts .tm-chart'))
  const out = []
  for (const card of cards) {
    const svg = card.querySelector('svg')
    if (!svg) continue
    const title = card.querySelector('.tm-chart-title')?.textContent || ''
    const xml = new XMLSerializer().serializeToString(svg)
    const svg64 = btoa(unescape(encodeURIComponent(xml)))
    const img = new Image()
    await new Promise(res => { img.onload = res; img.onerror = res; img.src = `data:image/svg+xml;base64,${svg64}` })
    const canvas = document.createElement('canvas')
    canvas.width = svg.clientWidth || 700; canvas.height = svg.clientHeight || 240
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    out.push({ title, data: canvas.toDataURL('image/png'), w: canvas.width, h: canvas.height })
  }
  return out
}

// Best-effort leaflet capture (tiles need crossOrigin, set on the TileLayer). Returns
// null on failure (e.g. a tile that won't rasterize) so the report still generates.
const captureMap = async () => {
  const el = document.querySelector('#tm-export-charts .tm-map') || document.querySelector('.tm-map')
  if (!el) return null
  try {
    const html2canvas = (await import('html2canvas')).default
    const canvas = await html2canvas(el, { useCORS: true, backgroundColor: '#ffffff', logging: false })
    return { data: canvas.toDataURL('image/png'), w: canvas.width, h: canvas.height }
  } catch (e) {
    return null
  }
}

// The utilization timeline is a div (not SVG) — capture via html2canvas like the map.
const captureUtilization = async () => {
  const el = document.querySelector('#tm-export-charts .tm-util')
  if (!el) return null
  try {
    const html2canvas = (await import('html2canvas')).default
    const canvas = await html2canvas(el, { useCORS: true, backgroundColor: '#ffffff', logging: false })
    return { data: canvas.toDataURL('image/png'), w: canvas.width, h: canvas.height }
  } catch (e) {
    return null
  }
}

const summaryText = (data, filter) => {
  const t = data.tiles || {}
  const speed = t.avg_passing_speed_mph != null ? `${t.avg_passing_speed_mph} mph` : 'n/a'
  const dist = t.avg_merging_distance_ft != null ? `${t.avg_merging_distance_ft} ft` : 'n/a'
  return `${t.track_count ?? 0} vehicles were recorded between ${filter.start} and ${filter.end} `
    + `across ${filter.units.length} unit(s). Average passing speed was ${speed}; `
    + `average merge distance was ${dist}.`
}

export const exportPdf = async (data, filter, meta = {}) => {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const W = doc.internal.pageSize.getWidth()
  const H = doc.internal.pageSize.getHeight()
  let y = 48
  doc.setFont('helvetica', 'bold'); doc.setFontSize(18)
  doc.text('Traffic Metrics Report', 40, y); y += 24
  doc.setFont('helvetica', 'normal'); doc.setFontSize(11)
  if (meta.company) { doc.text(`Group: ${meta.company}`, 40, y); y += 16 }
  doc.text(`Units: ${filter.units.join(', ')}`, 40, y); y += 16
  doc.text(`Period: ${filter.start} to ${filter.end}`, 40, y); y += 16
  doc.text(`Generated by ${meta.user || 'unknown'} on ${meta.date || ''}`, 40, y); y += 24
  doc.setFont('helvetica', 'bold'); doc.text('Summary', 40, y); y += 16
  doc.setFont('helvetica', 'normal')
  const lines = doc.splitTextToSize(summaryText(data, filter), W - 80)
  doc.text(lines, 40, y); y += lines.length * 14 + 12

  for (const img of await captureCharts()) {
    const w = W - 80, h = (img.h / img.w) * w
    if (y + h + 18 > H - 40) { doc.addPage(); y = 40 }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text(img.title, 40, y); y += 14
    doc.addImage(img.data, 'PNG', 40, y, w, h); y += h + 18
  }
  const util = await captureUtilization()
  if (util) {
    const w = W - 80, h = Math.min((util.h / util.w) * w, 340)
    if (y + h + 18 > H - 40) { doc.addPage(); y = 40 }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Utilization (uptime / armed)', 40, y); y += 14
    doc.addImage(util.data, 'PNG', 40, y, w, h); y += h + 18
  }
  const map = await captureMap()
  if (map) {
    const w = W - 80, h = Math.min((map.h / map.w) * w, 320)
    if (y + h + 18 > H - 40) { doc.addPage(); y = 40 }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Unit GPS coverage', 40, y); y += 14
    doc.addImage(map.data, 'PNG', 40, y, w, h)
  }
  doc.save(`traffic_${filter.start}_${filter.end}.pdf`)
}

export const exportPptx = async (data, filter, meta = {}) => {
  const PptxGenJS = (await import('pptxgenjs')).default
  const pptx = new PptxGenJS()
  const cover = pptx.addSlide()
  cover.addText('Traffic Metrics Report', { x: 0.5, y: 0.4, fontSize: 28, bold: true })
  const header = [
    meta.company ? `Group: ${meta.company}` : null,
    `Units: ${filter.units.join(', ')}`,
    `Period: ${filter.start} to ${filter.end}`,
    `Generated by ${meta.user || 'unknown'} on ${meta.date || ''}`,
  ].filter(Boolean).join('\n')
  cover.addText(header, { x: 0.5, y: 1.5, fontSize: 13, w: 9 })
  cover.addText(summaryText(data, filter), { x: 0.5, y: 3.2, fontSize: 13, w: 9 })

  for (const img of await captureCharts()) {
    const s = pptx.addSlide()
    s.addText(img.title, { x: 0.5, y: 0.3, fontSize: 16, bold: true })
    s.addImage({ data: img.data, x: 0.5, y: 0.9, w: 9, h: Math.min((img.h / img.w) * 9, 4.8) })
  }
  const util = await captureUtilization()
  if (util) {
    const s = pptx.addSlide()
    s.addText('Utilization (uptime / armed)', { x: 0.5, y: 0.3, fontSize: 16, bold: true })
    s.addImage({ data: util.data, x: 0.5, y: 0.9, w: 9, h: Math.min((util.h / util.w) * 9, 4.8) })
  }
  const map = await captureMap()
  if (map) {
    const s = pptx.addSlide()
    s.addText('Unit GPS coverage', { x: 0.5, y: 0.3, fontSize: 16, bold: true })
    s.addImage({ data: map.data, x: 0.5, y: 0.9, w: 9, h: Math.min((map.h / map.w) * 9, 4.8) })
  }
  await pptx.writeFile({ fileName: `traffic_${filter.start}_${filter.end}.pptx` })
}
